import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  getFirestore, 
  doc, 
  getDoc, 
  updateDoc, 
  serverTimestamp 
} from 'firebase/firestore';
import type { User } from 'firebase/auth';
import type { JobApplication, GenerateResumeResponse } from '@shared/types';
import { STATUS_LABELS, STATUS_COLORS } from '@shared/constants';
import GeneratedResumePreview from '../components/GeneratedResumePreview';
import { generateResume, analyzeJobDescription } from '../services/resumeMatchingFunctions';

interface ApplicationDetailsPageProps {
  user: User;
}

type GenerateStatus = 'idle' | 'generating' | 'done' | 'error';

export default function ApplicationDetailsPage({ user }: ApplicationDetailsPageProps) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [app, setApp] = useState<JobApplication | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // JD and Analysis states
  const [editingJd, setEditingJd] = useState(false);
  const [jdText, setJdText] = useState('');
  const [savingJd, setSavingJd] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);

  // Resume generation state
  const [genStatus, setGenStatus] = useState<GenerateStatus>('idle');
  const [genResult, setGenResult] = useState<GenerateResumeResponse | null>(null);
  const [genError, setGenError] = useState<string | null>(null);
  const [showInstructions, setShowInstructions] = useState(false);
  const [instructionOverride, setInstructionOverride] = useState('');

  const db = getFirestore();

  useEffect(() => {
    if (!id) return;
    loadApplication();
  }, [id]);

  const loadApplication = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const docRef = doc(db, 'applications', id);
      const snap = await getDoc(docRef);
      if (snap.exists()) {
        const data = snap.data() as JobApplication;
        if (data.userId !== user.uid) {
          setError('Unauthorized access');
        } else {
          setApp({ id: snap.id, ...data });
          setJdText(data.description || '');
        }
      } else {
        setError('Application not found');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load application');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveJd = async () => {
    if (!id || !jdText.trim()) return;
    setSavingJd(true);
    try {
      const docRef = doc(db, 'applications', id);
      await updateDoc(docRef, {
        description: jdText.trim(),
        updatedAt: serverTimestamp(),
      });
      setApp(prev => prev ? { ...prev, description: jdText.trim() } : null);
      setEditingJd(false);
    } catch (e: any) {
      alert(`Failed to save: ${e.message || 'Unknown error'}`);
    } finally {
      setSavingJd(false);
    }
  };

  const handleAnalyze = async () => {
    if (!id) return;
    setAnalyzing(true);
    try {
      await analyzeJobDescription(id);
      await loadApplication(); // Refresh data
    } catch (e: any) {
      alert(`Analysis failed: ${e.message || 'Unknown error'}`);
    } finally {
      setAnalyzing(false);
    }
  };

  const handleGenerateResume = async () => {
    if (!id) return;
    setGenStatus('generating');
    setGenResult(null);
    setGenError(null);
    try {
      const result = await generateResume(id, instructionOverride || undefined);
      setGenResult(result);
      setGenStatus('done');
    } catch (e: any) {
      setGenError(e.message || 'Resume generation failed. Please try again.');
      setGenStatus('error');
    }
  };

  const handleUseResume = async (downloadUrl: string, storagePath: string) => {
    if (!id) return;
    try {
      const docRef = doc(db, 'applications', id);
      await updateDoc(docRef, {
        resumeUrl: downloadUrl,
        resumePath: storagePath,
        updatedAt: serverTimestamp(),
      });
      alert('Resume attached to application successfully!');
      await loadApplication();
    } catch (err: any) {
      alert(`Failed to attach resume: ${err.message}`);
    }
  };

  const getScoreColor = (score: number): string => {
    if (score >= 70) return 'text-green-600 dark:text-green-400';
    if (score >= 50) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };

  const aDate = useMemo(() => {
    if (!app?.appliedDate) return 'N/A';
    const d = app.appliedDate instanceof Date ? app.appliedDate : (app.appliedDate as any)?.toDate?.();
    return d ? d.toLocaleDateString() : 'N/A';
  }, [app?.appliedDate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  if (error || !app) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col items-center justify-center p-4">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{error || 'Something went wrong'}</h1>
        <button onClick={() => navigate('/')} className="px-4 py-2 bg-indigo-600 text-white rounded-lg">
          Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 sticky top-0 z-20">
        <div className="max-w-screen-2xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/')} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
            <div className="min-w-0">
              <h1 className="text-xl font-bold text-gray-900 dark:text-white truncate">{app.jobTitle}</h1>
              <p className="text-sm text-gray-600 dark:text-gray-400">{app.company} • {app.location || 'Remote'}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className={`text-xs px-2 py-1 rounded font-medium ${STATUS_COLORS[app.status]}`}>
              {STATUS_LABELS[app.status]}
            </span>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 flex overflow-hidden">
        {/* Left Side: Application Info */}
        <div className="w-1/3 border-r border-gray-200 dark:border-gray-700 overflow-y-auto p-6 space-y-8 bg-white dark:bg-gray-800/50">
          <section>
            <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Essentials</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500 block">Applied Date</span>
                <span className="font-medium dark:text-white">{aDate}</span>
              </div>
              <div>
                <span className="text-gray-500 block">Source</span>
                <span className="font-medium dark:text-white capitalize">{app.source}</span>
              </div>
              {app.jobUrl && (
                <div className="col-span-2">
                  <span className="text-gray-500 block">Job URL</span>
                  <a href={app.jobUrl} target="_blank" rel="noopener noreferrer" className="text-indigo-600 dark:text-indigo-400 hover:underline truncate block">
                    {app.jobUrl}
                  </a>
                </div>
              )}
            </div>
          </section>

          <section>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-gray-900 dark:text-white">Job Description</h3>
              {!editingJd && (
                <button onClick={() => setEditingJd(true)} className="text-xs text-indigo-600 hover:underline transition-all">
                  Edit
                </button>
              )}
            </div>
            {editingJd ? (
              <div className="space-y-3">
                <textarea
                  value={jdText}
                  onChange={e => setJdText(e.target.value)}
                  className="w-full h-64 p-3 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500"
                />
                <div className="flex justify-end gap-2">
                  <button onClick={() => setEditingJd(false)} className="px-3 py-1.5 text-xs text-gray-600 dark:text-gray-400">Cancel</button>
                  <button 
                    onClick={handleSaveJd} 
                    disabled={savingJd}
                    className="px-3 py-1.5 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors"
                  >
                    {savingJd ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg text-sm text-gray-600 dark:text-gray-300 whitespace-pre-wrap max-h-96 overflow-y-auto border border-gray-200 dark:border-gray-700">
                {app.description || 'No description provided.'}
              </div>
            )}
          </section>

          <section className="space-y-4">
             <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold text-gray-900 dark:text-white">Analysis & Scoring</h3>
                <button 
                  onClick={handleAnalyze} 
                  disabled={analyzing || !app.description}
                  className="px-3 py-1.5 bg-indigo-600 text-white text-xs rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
                >
                  {analyzing ? 'Analyzing...' : 'Refresh Analysis'}
                </button>
             </div>

             {typeof app.matchScore === 'number' ? (
                <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-4 shadow-sm">
                   <div className="flex items-center justify-between mb-4">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Match Score</span>
                      <span className={`text-2xl font-bold ${getScoreColor(app.matchScore)}`}>{app.matchScore}%</span>
                   </div>
                   <div className="space-y-3">
                      <div>
                        <div className="flex justify-between text-xs mb-1">
                           <span className="text-gray-500">Skill Alignment</span>
                           <span className={getScoreColor(app.skillMatchScore || 0)}>{app.skillMatchScore}%</span>
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-gray-700 h-1 rounded-full overflow-hidden">
                           <div className="bg-green-500 h-full" style={{ width: `${app.skillMatchScore}%` }} />
                        </div>
                      </div>
                      <div>
                        <div className="flex justify-between text-xs mb-1">
                           <span className="text-gray-500">Semantic Fit</span>
                           <span className={getScoreColor(app.embeddingScore || 0)}>{app.embeddingScore}%</span>
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-gray-700 h-1 rounded-full overflow-hidden">
                           <div className="bg-blue-500 h-full" style={{ width: `${app.embeddingScore}%` }} />
                        </div>
                      </div>
                   </div>
                </div>
             ) : (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 p-4 rounded-lg text-xs text-yellow-800 dark:text-yellow-300">
                   Analyze the job description to see how your profile matches this role.
                </div>
             )}
          </section>

          {app.notes && (
            <section>
              <h3 className="text-sm font-bold dark:text-white mb-2">Notes</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 italic">"{app.notes}"</p>
            </section>
          )}
        </div>

        {/* Right Side: Resume Generation & Typst Editor */}
        <div className="flex-1 flex flex-col bg-gray-100 dark:bg-gray-900 p-6 overflow-y-auto">
          <div className="max-w-4xl w-full mx-auto space-y-6">
            <section className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-lg font-bold text-gray-900 dark:text-white">Tailored Resume</h2>
                  <p className="text-sm text-gray-500">Generate and refine a resume for this specific position.</p>
                </div>
                <button
                  onClick={() => setShowInstructions(!showInstructions)}
                  className="text-xs text-indigo-600 font-medium hover:underline"
                >
                  {showInstructions ? 'Hide Instructions' : 'Personalize Prompt'}
                </button>
              </div>

              {showInstructions && (
                <div className="mb-6">
                  <textarea
                    value={instructionOverride}
                    onChange={e => setInstructionOverride(e.target.value)}
                    placeholder="Focus on my Python expertise, keep it to one page, emphasize leadership roles..."
                    className="w-full h-24 p-4 text-sm border border-gray-200 dark:border-gray-700 rounded-xl bg-gray-50 dark:bg-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              )}

              {genStatus === 'idle' || genStatus === 'error' ? (
                <button
                  onClick={handleGenerateResume}
                  className="w-full py-4 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700 transition-all flex items-center justify-center gap-3 shadow-lg shadow-indigo-500/20"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  Generate Tailored Resume
                </button>
              ) : genStatus === 'generating' ? (
                <div className="flex flex-col items-center py-8">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-500 mb-4"></div>
                  <p className="text-gray-600 dark:text-gray-400 animate-pulse">Consulting LLM & Tailoring content...</p>
                </div>
              ) : null}

              {genStatus === 'error' && (
                <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-sm text-red-700 dark:text-red-400 flex gap-3">
                  <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  {genError}
                </div>
              )}
            </section>

            {genStatus === 'done' && genResult && (
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="bg-gray-50 dark:bg-gray-800/80 p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                   <h4 className="text-sm font-bold dark:text-white">Generated Document</h4>
                   <div className="flex gap-2">
                      <button 
                        onClick={() => handleUseResume(genResult.downloadUrl, genResult.storagePath)}
                        className="px-4 py-2 bg-green-600 text-white text-xs font-bold rounded-lg hover:bg-green-700 transition-all flex items-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Use This Resume
                      </button>
                   </div>
                </div>
                <div className="p-4">
                  <GeneratedResumePreview
                    applicationId={id || ''}
                    generatedResumeId={genResult.generatedResumeId}
                    downloadUrl={genResult.downloadUrl}
                    typstDownloadUrl={genResult.typstDownloadUrl}
                    retryReason={genResult.retryReason}
                    warnings={genResult.warnings}
                    onRegenerate={handleGenerateResume}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
